import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { supportTelegram } from '@/lib/legal'
import { opensTelegramApp, telegramAppUrl, telegramWebUrl } from '@/lib/telegram'

/**
 * The support bot on Telegram. A phone gets a link to t.me, which hands the chat to the Telegram app; a desktop gets
 * a button that offers Telegram Web or the installed application, because t.me alone opens nothing where the app is
 * missing. It sits beside the chat button, its menu opening above it.
 */
export function TelegramLink({ className, children }: { className?: string; children: ReactNode }) {
  const id = useId()
  const [direct] = useState(opensTelegramApp)
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLSpanElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const first = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false) }
    document.addEventListener('pointerdown', away)
    first.current?.focus()
    return () => document.removeEventListener('pointerdown', away)
  }, [open])

  if (!supportTelegram) return null
  if (direct) return <a className={className} href={telegramAppUrl} target="_blank" rel="noreferrer">{children}</a>

  function close(refocus: boolean) {
    setOpen(false)
    if (refocus) trigger.current?.focus()
  }

  function onKeyDown(event: KeyboardEvent<HTMLSpanElement>) {
    if (!open) return
    if (event.key === 'Escape') { event.preventDefault(); close(true); return }
    if (event.key === 'Tab') { close(false); return }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const items = Array.from(root.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]') ?? [])
      const index = items.indexOf(document.activeElement as HTMLAnchorElement)
      items[(index + (event.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus()
    }
  }

  return <span ref={root} className="telegram-link" onKeyDown={onKeyDown}>
    <button ref={trigger} type="button" className={className} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? `${id}-menu` : undefined} onClick={() => setOpen(!open)}>{children}</button>
    {open && <span id={`${id}-menu`} role="menu" aria-label="Abrir Telegram" className="telegram-menu">
      <a ref={first} role="menuitem" href={telegramWebUrl} target="_blank" rel="noreferrer" onClick={() => close(false)}><strong>Telegram Web</strong><span>En el navegador, sin instalar nada.</span></a>
      <a role="menuitem" href={telegramAppUrl} target="_blank" rel="noreferrer" onClick={() => close(false)}><strong>Aplicación de Telegram</strong><span>Si la tienes instalada.</span></a>
    </span>}
  </span>
}
